
import React, { useState, useCallback } from 'react';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTeacherMatch } from '@/hooks/useTeacherMatch';
import { TeacherMatchFilters } from '@/components/admin/teacher-match/TeacherMatchFilters';
import { TeacherMatchCard } from '@/components/admin/teacher-match/TeacherMatchCard';
import { Search, CheckSquare, Square, Send, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const AdminTeacherMatchPage: React.FC = () => {
  const {
    teachers,
    totalCount,
    isLoading,
    error,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    filterOptions,
    getMatchedAttributes,
  } = useTeacherMatch();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(teachers.map(t => t.id)));
  }, [teachers]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleSend = async () => {
    if (selectedIds.size === 0) return;
    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('match-teachers', {
        body: { teacher_ids: Array.from(selectedIds) },
      });
      if (error) throw error;
      toast({ title: 'สำเร็จ', description: `ส่งครู ${selectedIds.size} คนเรียบร้อย` });
      setSelectedIds(new Set());
    } catch (err: any) {
      toast({ title: 'เกิดข้อผิดพลาด', description: err.message, variant: 'destructive' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        <main className="flex-1 p-6 space-y-6 overflow-auto">
          <div>
            <h1 className="text-2xl font-bold text-foreground">แมทช์ครู</h1>
            <p className="text-sm text-muted-foreground">ค้นหาและกรองครูตามคุณสมบัติต่างๆ</p>
          </div>

          <TeacherMatchFilters
            filters={filters}
            filterOptions={filterOptions}
            onUpdateFilters={updateFilters}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            resultCount={teachers.length}
            totalCount={totalCount}
          />

          {/* Loading */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-40 w-full rounded-lg" />
              ))}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-12 text-destructive">
              <p>เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
              <p className="text-sm">{(error as Error).message}</p>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && teachers.length === 0 && (
            <div className="text-center py-16 space-y-3">
              <Search className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <p className="text-lg font-medium text-muted-foreground">ไม่พบครูที่ตรงกับเงื่อนไข</p>
              <p className="text-sm text-muted-foreground">ลองปรับตัวกรองใหม่</p>
            </div>
          )}

          {/* Selection Toolbar + Results */}
          {!isLoading && !error && teachers.length > 0 && (
            <>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
                <Button variant="outline" size="sm" onClick={selectAll}>
                  <CheckSquare className="h-4 w-4 mr-1" />
                  เลือกทั้งหมด
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAll} disabled={selectedIds.size === 0}>
                  <Square className="h-4 w-4 mr-1" />
                  ยกเลิกทั้งหมด
                </Button>
                <span className="text-sm text-muted-foreground ml-auto">
                  เลือกแล้ว <span className="font-semibold text-foreground">{selectedIds.size}</span> คน
                </span>
                <Button size="sm" onClick={handleSend} disabled={selectedIds.size === 0 || isSending}>
                  {isSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  ส่งครูที่เลือก
                </Button>
              </div>

              <div className="space-y-3">
                {teachers.map(teacher => (
                  <TeacherMatchCard
                    key={teacher.id}
                    teacher={teacher}
                    matchedAttributes={getMatchedAttributes(teacher)}
                    selected={selectedIds.has(teacher.id)}
                    onToggleSelect={() => toggleSelect(teacher.id)}
                  />
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminTeacherMatchPage;
